/**
 * BM-7639 — Three-checkbox popover (All / Active / Selected) attached to a column header.
 * Reads ticked scopes from Redux, writes back through toggleColumnTotalScope + fetchColumnTotals,
 * and persists per-(rangeplan × user × column) in localStorage.
 */

import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Popover from 'react-bootstrap/Popover';
import { fetchColumnTotals, toggleColumnTotalScope } from '../../store/ProductList.action';
import { saveColumnScopes } from '../../utils/columnTotalsStorage';
import { SCOPE_KEYS, SCOPE_LABELS } from './scopeColors';
import calculateIcon from '../../../../../assets/icons/filter-icons/col-filter-calclulator.svg';

function ColumnCalculationPopOver({ columnId, rangeplanId, userId, filters }) {
  const dispatch = useDispatch();
  const tickedScopes = useSelector((state) => state.ProductListData?.columnTotals?.[columnId]?.scopes ?? []);
  const tickedSet = useMemo(() => new Set(tickedScopes), [tickedScopes]);

  const filtersRef = useRef(filters || null);
  useEffect(() => {
    filtersRef.current = filters || null;
  }, [filters]);

  const handleToggle = useCallback(
    (scope) => {
      const wasTicked = tickedSet.has(scope);
      const nextScopes = wasTicked ? tickedScopes.filter((s) => s !== scope) : [...tickedScopes, scope];

      dispatch(toggleColumnTotalScope(columnId, nextScopes));
      saveColumnScopes(rangeplanId, userId, columnId, nextScopes);

      if (!wasTicked && scope === 'all') {
        dispatch(
          fetchColumnTotals({
            rangeplanId,
            requests: [{ columnId, scopes: [scope] }],
            filters: filtersRef.current,
          }),
        );
      }
    },
    [columnId, dispatch, rangeplanId, tickedScopes, tickedSet, userId],
  );

  const popover = (
    <Popover className="rp-column-filter-popover" id={`column-calc-popover-${columnId}`}>
      <Popover.Content>
        <div className="column-calc-popover">
          <div className="column-calc-popover__title">
            <img src={calculateIcon} />
            Column Calculations
          </div>
          {SCOPE_KEYS.map((scope) => {
            const inputId = `column-calc-${columnId}-${scope}`;
            return (
              <div key={scope} className="column-calc-popover__row">
                <span className={`column-calc-popover__swatch column-calc-popover__swatch--${scope}`} aria-hidden="true" />
                <span className="column-calc-popover__label">{SCOPE_LABELS[scope]}</span>
                <div className="common-checkbox size-sm">
                  <input id={inputId} type="checkbox" checked={tickedSet.has(scope)} onChange={() => handleToggle(scope)} />
                  <label htmlFor={inputId} className="chklabel" />
                </div>
              </div>
            );
          })}
        </div>
      </Popover.Content>
    </Popover>
  );

  const triggerActive = tickedScopes.length > 0;

  return (
    <OverlayTrigger trigger="click" placement="bottom" overlay={popover} rootClose>
      <button type="button" className={`column-calc-trigger${triggerActive ? ' column-calc-trigger--active' : ''}`} aria-label="Column calculations">
        <i className="icon-More f-20" />
      </button>
    </OverlayTrigger>
  );
}

export default ColumnCalculationPopOver;
